import React, { useState, useRef, useLayoutEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  ExpandLess, ExpandMore,
  Home, Business, AccountBalance, BarChart, People,
} from "@mui/icons-material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ShoppingCartIcon        from "@mui/icons-material/ShoppingCart";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShoppingBasketIcon      from "@mui/icons-material/ShoppingBasket";
import { useAuth } from "./AuthContext";

// ── Design tokens (matches Home.js) ───────────────────────────────────────────
const C = {
  bg:         "linear-gradient(180deg, #0f2027 0%, #0a1a1a 100%)",
  surface:    "rgba(255,255,255,0.04)",
  surfaceHov: "rgba(45,212,191,0.10)",
  active:     "rgba(45,212,191,0.14)",
  accent:     "#2dd4bf",
  text:       "#e2f0ef",
  muted:      "#7fa8a8",
  divider:    "rgba(255,255,255,0.07)",
  shadow:     "4px 0 24px rgba(0,0,0,0.45)",
};

const W_OPEN   = 228;
const W_CLOSED = 56;

// ── Smooth animated collapse ───────────────────────────────────────────────────
const AnimCollapse = ({ open, children }) => {
  const innerRef = useRef(null);
  const outerRef = useRef(null);

  // Use "none" max-height when open so nested expansions grow freely
  // Use "0px" when closed to hide content
  useLayoutEffect(() => {
    if (!outerRef.current || !innerRef.current) return;
    if (open) {
      // Animate from current height to scrollHeight, then remove cap so
      // nested collapses can expand freely afterwards
      const h = innerRef.current.scrollHeight;
      outerRef.current.style.maxHeight = h + "px";
      const tid = setTimeout(() => {
        if (outerRef.current) outerRef.current.style.maxHeight = "none";
      }, 280); // slightly longer than transition duration
      return () => clearTimeout(tid);
    } else {
      // Snap to current rendered height first so the transition has a start point
      outerRef.current.style.maxHeight = outerRef.current.scrollHeight + "px";
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (outerRef.current) outerRef.current.style.maxHeight = "0px";
        });
      });
    }
  }, [open]);

  return (
    <div
      ref={outerRef}
      style={{
        overflow:   "hidden",
        maxHeight:  "0px",
        transition: "max-height 0.26s cubic-bezier(.4,0,.2,1)",
      }}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
};

// ── Leaf nav link ──────────────────────────────────────────────────────────────
const NavItem = ({ to, label, icon, sidebarOpen, indent = false, color = C.accent }) => {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <NavLink to={to} style={{ textDecoration: "none", display: "block" }}>
      <div
        className="__sb-item"
        style={{
          display:        "flex",
          alignItems:     "center",
          gap:            sidebarOpen ? 9 : 0,
          justifyContent: sidebarOpen ? "flex-start" : "center",
          padding:        sidebarOpen
            ? (indent ? "4px 10px 4px 40px" : "6px 10px")
            : "6px 0",
          margin:         "1px 5px",
          borderRadius:   6,
          cursor:         "pointer",
          background:     isActive ? C.active : "transparent",
          borderLeft:     isActive ? `2px solid ${C.accent}` : "2px solid transparent",
          transition:     "background 0.13s, border-color 0.13s",
          position:       "relative",
        }}
        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = C.surfaceHov; }}
        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
      >
        {/* Icon */}
        {icon && (
          <span style={{
            color:     isActive ? C.accent : color,
            display:   "flex",
            flexShrink: 0,
            fontSize:  indent ? 15 : 18,
          }}>
            {icon}
          </span>
        )}

        {/* Label */}
        {sidebarOpen && (
          <span style={{
            fontSize:     indent ? 11.5 : 12.5,
            fontWeight:   indent ? 500 : 700,
            fontFamily:   "Inter, sans-serif",
            color:        isActive ? C.accent : C.text,
            whiteSpace:   "nowrap",
            overflow:     "hidden",
            textOverflow: "ellipsis",
            letterSpacing: "0.15px",
          }}>
            {label}
          </span>
        )}

        {/* Tooltip when collapsed */}
        {!sidebarOpen && (
          <span className="__sb-tooltip" style={{
            position:      "absolute",
            left:          W_CLOSED + 8,
            top:           "50%",
            transform:     "translateY(-50%)",
            background:    "#1a3535",
            color:         C.text,
            fontSize:      11,
            fontFamily:    "Inter, sans-serif",
            padding:       "4px 10px",
            borderRadius:  6,
            whiteSpace:    "nowrap",
            pointerEvents: "none",
            opacity:       0,
            transition:    "opacity 0.15s",
            zIndex:        9999,
            boxShadow:     "0 4px 14px rgba(0,0,0,0.45)",
            border:        `1px solid ${C.divider}`,
          }}>
            {label}
          </span>
        )}
      </div>
    </NavLink>
  );
};

// ── Collapsible section header ─────────────────────────────────────────────────
const SectionHeader = ({ icon, label, color = C.accent, sidebarOpen, isOpen, onClick }) => (
  <div
    onClick={onClick}
    style={{
      display:        "flex",
      alignItems:     "center",
      gap:            sidebarOpen ? 9 : 0,
      justifyContent: sidebarOpen ? "flex-start" : "center",
      padding:        sidebarOpen ? "6px 10px" : "6px 0",
      margin:         "1px 5px",
      borderRadius:   6,
      cursor:         "pointer",
      transition:     "background 0.13s",
      userSelect:     "none",
    }}
    onMouseEnter={e => { e.currentTarget.style.background = C.surfaceHov; }}
    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
  >
    <span style={{ color, display: "flex", flexShrink: 0, fontSize: 18 }}>{icon}</span>
    {sidebarOpen && (
      <>
        <span style={{
          flex:          1,
          fontSize:      12.5,
          fontWeight:    700,
          fontFamily:    "Inter, sans-serif",
          color:         C.text,
          letterSpacing: "0.2px",
        }}>
          {label}
        </span>
        <span style={{ color: C.muted, display: "flex", fontSize: 16 }}>
          {isOpen ? <ExpandLess fontSize="inherit" /> : <ExpandMore fontSize="inherit" />}
        </span>
      </>
    )}
  </div>
);

// ── Nested sub-group header ────────────────────────────────────────────────────
const SubGroupHeader = ({ icon, label, color, sidebarOpen, isOpen, onClick }) => (
  <div
    onClick={onClick}
    style={{
      display:        "flex",
      alignItems:     "center",
      gap:            sidebarOpen ? 8 : 0,
      justifyContent: sidebarOpen ? "flex-start" : "center",
      padding:        sidebarOpen ? "4px 10px 4px 26px" : "4px 0",
      margin:         "1px 5px",
      borderRadius:   6,
      cursor:         "pointer",
      transition:     "background 0.13s",
      userSelect:     "none",
    }}
    onMouseEnter={e => { e.currentTarget.style.background = C.surfaceHov; }}
    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
  >
    <span style={{ color, display: "flex", flexShrink: 0, fontSize: 15 }}>{icon}</span>
    {sidebarOpen && (
      <>
        <span style={{ flex: 1, fontSize: 11.5, fontWeight: 600, fontFamily: "Inter, sans-serif", color: C.text }}>
          {label}
        </span>
        <span style={{ color: C.muted, display: "flex", fontSize: 14 }}>
          {isOpen ? <ExpandLess fontSize="inherit" /> : <ExpandMore fontSize="inherit" />}
        </span>
      </>
    )}
  </div>
);

// ── Thin divider ───────────────────────────────────────────────────────────────
const SectionDivider = () => (
  <div style={{ height: 1, background: C.divider, margin: "5px 12px" }} />
);

// ── Group label pill (only when open) ─────────────────────────────────────────
const GroupLabel = ({ label, color, sidebarOpen }) => {
  if (!sidebarOpen) return null;
  return (
    <div style={{
      padding:       "2px 12px 1px 14px",
      marginTop:     4,
    }}>
      <span style={{
        fontSize:      9,
        fontWeight:    800,
        fontFamily:    "Inter, sans-serif",
        color,
        textTransform: "uppercase",
        letterSpacing: "0.8px",
        opacity:       0.7,
      }}>
        {label}
      </span>
    </div>
  );
};

// ── Main Sidebar ───────────────────────────────────────────────────────────────
const Sidebar = ({ open }) => {
  const [masterOpen,  setMasterOpen]  = useState(false);
  const [txOpen,      setTxOpen]      = useState(false);
  const [accOpen,     setAccOpen]     = useState(false);
  const [userOpen,    setUserOpen]    = useState(false);
  const [reportOpen,  setReportOpen]  = useState(false);
  const [subPurchase, setSubPurchase] = useState(false);
  const [subSale,     setSubSale]     = useState(false);
  const [subAccRep,   setSubAccRep]   = useState(false);

  const { permissions } = useAuth();
  const hasMaster  = permissions.includes("Read Master");
  const hasAcc     = permissions.includes("Read Accounts");
  const hasAdmin   = permissions.includes("Read Administration");
  const hasReports = permissions.includes("Read Reports");

  const w = open ? W_OPEN : W_CLOSED;

  return (
    <>
      <style>{`
        .__sb-item:hover .__sb-tooltip { opacity: 1 !important; }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(45,212,191,0.2); border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(45,212,191,0.4); }
      `}</style>

      <div style={{
        position:      "fixed",
        top:           0,
        left:          0,
        width:         w,
        height:        "100vh",
        background:    C.bg,
        boxShadow:     C.shadow,
        zIndex:        1199,
        display:       "flex",
        flexDirection: "column",
        transition:    "width 0.26s cubic-bezier(.4,0,.2,1)",
        overflow:      "hidden",
        fontFamily:    "Inter, sans-serif",
        borderRight:   "1px solid rgba(45,212,191,0.08)",
      }}>

        {/* Scrollable nav area */}
        <div style={{
          flex:       1,
          overflowY:  "auto",
          overflowX:  "hidden",
          paddingTop: 62,
          paddingBottom: 16,
        }}>

          {/* Home */}
          <NavItem
            to="/Home"
            label="Home"
            icon={<Home fontSize="inherit" />}
            sidebarOpen={open}
            color={C.accent}
          />

          <SectionDivider />

          {/* ── Master ── */}
          {hasMaster && (
            <>
              <GroupLabel label="Master" color="#60a5fa" sidebarOpen={open} />
              <SectionHeader
                icon={<Business fontSize="inherit" />}
                label="Master"
                color="#60a5fa"
                sidebarOpen={open}
                isOpen={masterOpen}
                onClick={() => setMasterOpen(v => !v)}
              />
              <AnimCollapse open={masterOpen && open}>
                <NavItem to="/CompanyEdit"       label="Company"       icon={<Business fontSize="inherit" />}       sidebarOpen={open} indent color="#60a5fa" />
                <NavItem to="/BalanceSheetIndex" label="Balance Sheet" icon={<AccountBalance fontSize="inherit" />} sidebarOpen={open} indent color="#60a5fa" />
                <NavItem to="/MasterIndex"       label="Registration"  icon={<People fontSize="inherit" />}         sidebarOpen={open} indent color="#60a5fa" />
                <NavItem to="/CategoryIndex"     label="Category"      icon={<BarChart fontSize="inherit" />}       sidebarOpen={open} indent color="#60a5fa" />
                <NavItem to="/ItemIndex"         label="Item"          icon={<ShoppingCartIcon fontSize="inherit" />} sidebarOpen={open} indent color="#60a5fa" />
                <NavItem to="/StoreLocationIndex"label="Store Location"icon={<LocationOnIcon fontSize="inherit" />} sidebarOpen={open} indent color="#60a5fa" />
              </AnimCollapse>
            </>
          )}

          <SectionDivider />

          {/* ── Transaction ── */}
          {hasMaster && (
            <>
              <GroupLabel label="Transaction" color="#a78bfa" sidebarOpen={open} />
              <SectionHeader
                icon={<AccountBalance fontSize="inherit" />}
                label="Transaction"
                color="#a78bfa"
                sidebarOpen={open}
                isOpen={txOpen}
                onClick={() => setTxOpen(v => !v)}
              />
              <AnimCollapse open={txOpen && open}>
                <NavItem to="/PurchaseIndex"       label="Purchase"        icon={<ShoppingCartIcon fontSize="inherit" />}   sidebarOpen={open} indent color="#a78bfa" />
                <NavItem to="/PurchaseOrderIndex"  label="Purchase Order"  icon={<ShoppingCartIcon fontSize="inherit" />}   sidebarOpen={open} indent color="#a78bfa" />
                <NavItem to="/PurchaseReturnIndex" label="Purchase Return" icon={<ShoppingCartIcon fontSize="inherit" />}   sidebarOpen={open} indent color="#a78bfa" />
                <NavItem to="/ProformaIndex"       label="Proforma"        icon={<ShoppingBasketIcon fontSize="inherit" />} sidebarOpen={open} indent color="#a78bfa" />
                <NavItem to="/SaleIndex"           label="Sale"            icon={<ShoppingBasketIcon fontSize="inherit" />} sidebarOpen={open} indent color="#a78bfa" />
                <NavItem to="/SaleReturnIndex"     label="Sale Return"     icon={<ShoppingBasketIcon fontSize="inherit" />} sidebarOpen={open} indent color="#a78bfa" />
                <NavItem to="/StockJournalIndex"   label="Stock Journal"   icon={<ShoppingBasketIcon fontSize="inherit" />} sidebarOpen={open} indent color="#a78bfa" />
                <NavItem to="/StockTransferIndex"  label="Stock Transfer"  icon={<ShoppingBasketIcon fontSize="inherit" />} sidebarOpen={open} indent color="#a78bfa" />
              </AnimCollapse>
            </>
          )}

          <SectionDivider />

          {/* ── Accounts ── */}
          {hasAcc && (
            <>
              <GroupLabel label="Accounts" color="#34d399" sidebarOpen={open} />
              <SectionHeader
                icon={<AccountBalanceWalletIcon fontSize="inherit" />}
                label="Accounts"
                color="#34d399"
                sidebarOpen={open}
                isOpen={accOpen}
                onClick={() => setAccOpen(v => !v)}
              />
              <AnimCollapse open={accOpen && open}>
                <NavItem to="/JournelEntryIndex"      label="Journal Entry"     icon={<AccountBalance fontSize="inherit" />}        sidebarOpen={open} indent color="#34d399" />
                <NavItem to="/IncomeExpenseIndex"     label="Income & Expense"  icon={<AccountBalanceWalletIcon fontSize="inherit" />} sidebarOpen={open} indent color="#34d399" />
                <NavItem to="/PaymentIndex"           label="Purchase Payment"  icon={<AccountBalanceWalletIcon fontSize="inherit" />} sidebarOpen={open} indent color="#34d399" />
                <NavItem to="/ReceiptIndex"           label="Sale Receipt"      icon={<AccountBalanceWalletIcon fontSize="inherit" />} sidebarOpen={open} indent color="#34d399" />
                <NavItem to="/PaymentAndReceiptIndex" label="Payment & Receipt" icon={<AccountBalanceWalletIcon fontSize="inherit" />} sidebarOpen={open} indent color="#34d399" />
              </AnimCollapse>
            </>
          )}

          <SectionDivider />

          {/* ── Auth Zen ── */}
          {hasAdmin && (
            <>
              <GroupLabel label="Administration" color="#f472b6" sidebarOpen={open} />
              <SectionHeader
                icon={<People fontSize="inherit" />}
                label="Auth Zen"
                color="#f472b6"
                sidebarOpen={open}
                isOpen={userOpen}
                onClick={() => setUserOpen(v => !v)}
              />
              <AnimCollapse open={userOpen && open}>
                <NavItem to="/UserIndex"      label="Users"          icon={<People fontSize="inherit" />}   sidebarOpen={open} indent color="#f472b6" />
                <NavItem to="/RoleIndex"      label="Roles"          icon={<Business fontSize="inherit" />} sidebarOpen={open} indent color="#f472b6" />
                <NavItem to="/PasswordChange" label="Reset Password" icon={<People fontSize="inherit" />}   sidebarOpen={open} indent color="#f472b6" />
              </AnimCollapse>
            </>
          )}

          <SectionDivider />

          {/* ── Reports ── */}
          {hasReports && (
            <>
              <GroupLabel label="Reports" color="#fb923c" sidebarOpen={open} />
              <SectionHeader
                icon={<BarChart fontSize="inherit" />}
                label="Reports"
                color="#fb923c"
                sidebarOpen={open}
                isOpen={reportOpen}
                onClick={() => setReportOpen(v => !v)}
              />
              <AnimCollapse open={reportOpen && open}>

                {/* Purchase sub-group */}
                <SubGroupHeader
                  icon={<ShoppingCartIcon fontSize="inherit" />}
                  label="Purchase"
                  color="#fb923c"
                  sidebarOpen={open}
                  isOpen={subPurchase}
                  onClick={e => { e.stopPropagation(); setSubPurchase(v => !v); }}
                />
                <AnimCollapse open={subPurchase && open}>
                  <NavItem to="/PurchaseRegisterReport"         label="Purchase Register" icon={<ShoppingCartIcon fontSize="inherit" />}   sidebarOpen={open} indent color="#fb923c" />
                  <NavItem to="/PurchasePartyandItemWiseReport" label="Purchase Report"   icon={<ShoppingCartIcon fontSize="inherit" />}   sidebarOpen={open} indent color="#fb923c" />
                </AnimCollapse>

                {/* Sale sub-group */}
                <SubGroupHeader
                  icon={<ShoppingBasketIcon fontSize="inherit" />}
                  label="Sales"
                  color="#4ade80"
                  sidebarOpen={open}
                  isOpen={subSale}
                  onClick={e => { e.stopPropagation(); setSubSale(v => !v); }}
                />
                <AnimCollapse open={subSale && open}>
                  <NavItem to="/SaleRegister"           label="Sale Register"     icon={<ShoppingBasketIcon fontSize="inherit" />} sidebarOpen={open} indent color="#4ade80" />
                  <NavItem to="/Salegstreport"          label="Sale GST Report"   icon={<ShoppingBasketIcon fontSize="inherit" />} sidebarOpen={open} indent color="#4ade80" />
                  <NavItem to="/PartyandItemWiseReport" label="Sales Report"      icon={<ShoppingBasketIcon fontSize="inherit" />} sidebarOpen={open} indent color="#4ade80" />
                  <NavItem to="/SaleAnalysisReport"     label="Sale Analysis"     icon={<ShoppingBasketIcon fontSize="inherit" />} sidebarOpen={open} indent color="#4ade80" />
                  <NavItem to="/DailySaleProfitReport"  label="Daily Sale Profit" icon={<ShoppingBasketIcon fontSize="inherit" />} sidebarOpen={open} indent color="#4ade80" />
                  <NavItem to="/SaleHSNCReport"         label="Sale HSNC"         icon={<ShoppingBasketIcon fontSize="inherit" />} sidebarOpen={open} indent color="#4ade80" />
                  <NavItem to="/Stock"                  label="Stock"             icon={<ShoppingBasketIcon fontSize="inherit" />} sidebarOpen={open} indent color="#4ade80" />
                  <NavItem to="/StockAll"               label="Stock All"         icon={<ShoppingBasketIcon fontSize="inherit" />} sidebarOpen={open} indent color="#4ade80" />
                  <NavItem to="/SalePaymentWise"        label="Sale PaymentWise"  icon={<ShoppingBasketIcon fontSize="inherit" />} sidebarOpen={open} indent color="#4ade80" />
                </AnimCollapse>

                {/* Accounts sub-group */}
                <SubGroupHeader
                  icon={<AccountBalanceWalletIcon fontSize="inherit" />}
                  label="Accounts"
                  color="#c084fc"
                  sidebarOpen={open}
                  isOpen={subAccRep}
                  onClick={e => { e.stopPropagation(); setSubAccRep(v => !v); }}
                />
                <AnimCollapse open={subAccRep && open}>
                  <NavItem to="/CashBook"                  label="Cash Book"            icon={<AccountBalanceWalletIcon fontSize="inherit" />} sidebarOpen={open} indent color="#c084fc" />
                  <NavItem to="/CreditorDebitorReport"     label="Creditors/Debtors"    icon={<AccountBalanceWalletIcon fontSize="inherit" />} sidebarOpen={open} indent color="#c084fc" />
                  <NavItem to="/CustomerOutstandingReport" label="Customer Outstanding" icon={<AccountBalanceWalletIcon fontSize="inherit" />} sidebarOpen={open} indent color="#c084fc" />
                  <NavItem to="/Ledger"                    label="Ledger"               icon={<AccountBalanceWalletIcon fontSize="inherit" />} sidebarOpen={open} indent color="#c084fc" />
                  <NavItem to="/SupplierOutstandingReport" label="Supplier Outstanding" icon={<AccountBalanceWalletIcon fontSize="inherit" />} sidebarOpen={open} indent color="#c084fc" />
                  <NavItem to="/TrialBalanceReport"        label="Trial Balance"        icon={<AccountBalanceWalletIcon fontSize="inherit" />} sidebarOpen={open} indent color="#c084fc" />
                </AnimCollapse>


              </AnimCollapse>

            </>
          )}

        </div>
      </div>
    </>
  );
};

export default Sidebar;
